import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Briefcase,
  Plus,
  ClipboardCheck,
  FileText,
  UserPlus,
  Receipt,
  Activity,
  Rocket,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Progress } from '@/app/components/ui/progress';
import { usePlatformDashboard } from '@/hooks/use-platform';
import { usePermissions } from '@/hooks/use-permissions';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import { activityHeadline, activityModelLabel } from '@/lib/labels';
import { useOnboardingState } from '@/hooks/use-onboarding';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

function CardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-5 w-24" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-28" />
      </CardContent>
    </Card>
  );
}

export function PlatformDashboardPage() {
  const orgTimeZone = useOrgTimeZone();
  const orgCurrency = useOrgCurrency();
  const { data, isLoading, isError } = usePlatformDashboard();
  const { data: onboardingData } = useOnboardingState();
  const { isAdmin, canCreateTechs } = usePermissions();

  const showSetupBanner =
    onboardingData?.is_admin === true && onboardingData?.onboarding_completed === false;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">Overview across all H3ight modules.</p>
        </div>
      </div>

      {showSetupBanner && (
        <div
          data-testid="finish-setup-banner"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Rocket className="h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Your setup isn&apos;t complete yet</p>
                <p className="text-sm text-amber-700">
                  {onboardingData!.completed_count} of {onboardingData!.total_count} steps
                  done &mdash; pick up where you left off.
                </p>
              </div>
            </div>
            <Button asChild size="sm" className="shrink-0 bg-amber-600 hover:bg-amber-700">
              <Link to="/onboarding">Finish Setup</Link>
            </Button>
          </div>
          <Progress
            value={onboardingData!.progress}
            className="mt-3 h-2"
          />
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
            <p className="text-gray-600">Unable to load dashboard data</p>
          </CardContent>
        </Card>
      ) : data ? (
        <>
          {/* Module Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.cards.h3gear && (
              <Link to="/assets" className="block">
                <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <img src="/logos/h3gear.svg" alt="H3Gear" className="h-5" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Assets</span>
                      <span className="text-lg font-bold">{data.cards.h3gear.total_assets}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Overdue Inspections</span>
                      <span className={`text-lg font-bold ${data.cards.h3gear.overdue_inspections > 0 ? 'text-orange-600' : ''}`}>
                        {data.cards.h3gear.overdue_inspections}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Deployed Kits</span>
                      <span className="text-lg font-bold">{data.cards.h3gear.deployed_kits}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            {data.cards.h3safe && (
              <Link to="/safety" className="block">
                <Card className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <img src="/logos/h3safe.svg" alt="H3Safe" className="h-5" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Active JSAs</span>
                      <span className="text-lg font-bold">{data.cards.h3safe.active_jsas}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Pending Signatures</span>
                      <span className={`text-lg font-bold ${data.cards.h3safe.pending_signatures > 0 ? 'text-yellow-600' : ''}`}>
                        {data.cards.h3safe.pending_signatures}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Open Near-Misses</span>
                      <span className={`text-lg font-bold ${data.cards.h3safe.open_near_misses > 0 ? 'text-red-600' : ''}`}>
                        {data.cards.h3safe.open_near_misses}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Approved SWPs</span>
                      <span className="text-lg font-bold">{data.cards.h3safe.active_swps}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            {data.cards.h3crew && (
              <Link to="/crew" className="block">
                <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <img src="/logos/h3crew.svg" alt="H3Crew" className="h-5" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Active Techs</span>
                      <span className="text-lg font-bold">{data.cards.h3crew.active_techs}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Expiring Certs (60d)</span>
                      <span className={`text-lg font-bold ${data.cards.h3crew.expiring_certs > 0 ? 'text-orange-600' : ''}`}>
                        {data.cards.h3crew.expiring_certs}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Currently Assigned</span>
                      <span className="text-lg font-bold">{data.cards.h3crew.techs_currently_assigned}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            {data.cards.h3project && (
              <Link to="/projects" className="block">
                <Card className="border-l-4 border-l-amber-500 hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <img src="/logos/h3books.svg" alt="H3Books" className="h-5" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Active Projects</span>
                      <span className="text-lg font-bold">{data.cards.h3project.active_projects}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Pending Bids</span>
                      <span className="text-lg font-bold">{data.cards.h3project.pending_bids}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Overdue Invoices</span>
                      <span className={`text-lg font-bold ${data.cards.h3project.overdue_invoices > 0 ? 'text-red-600' : ''}`}>
                        {data.cards.h3project.overdue_invoices}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Outstanding</span>
                      <span className="text-lg font-bold">
                        {formatMoney(data.cards.h3project.total_outstanding, orgCurrency)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            {data.cards.h3pay && (
              <Link to="/payroll" className="block">
                <Card className="border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <img src="/logos/h3pay.svg" alt="H3Pay" className="h-5" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Pending Expenses</span>
                      <span className="text-lg font-bold">{data.cards.h3pay.pending_expenses}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Open Pay Periods</span>
                      <span className="text-lg font-bold" data-testid="h3pay-open-pay-periods">
                        {data.cards.h3pay.open_pay_periods}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Unbilled Expenses</span>
                      <span className="text-lg font-bold" data-testid="h3pay-unbilled-amount">
                        {formatMoney(data.cards.h3pay.unbilled_amount, orgCurrency)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>

          {/* Recent Activity Feed */}
          {isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
                <CardDescription>Latest actions across all modules</CardDescription>
              </CardHeader>
              <CardContent>
                {data.recent_activity && data.recent_activity.length > 0 ? (
                  <div className="space-y-3">
                    {data.recent_activity.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="p-1.5 bg-gray-100 rounded-lg mt-0.5">
                          <ActivityIcon action={item.action} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">
                            {item.message ?? activityHeadline(item.action, item.model_type)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.user ?? 'System'} &middot;{' '}
                            {formatDateTimeInTimeZone(item.timestamp, orgTimeZone)}
                          </p>
                        </div>
                        <Badge variant="outline" className="capitalize text-xs">
                          {activityModelLabel(item.model_type)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">No recent activity</div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                {data.enabled_modules.inventory && (
                  <>
                    <Button asChild variant="outline" className="h-auto flex-col p-4">
                      <Link to="/assets/new">
                        <Plus className="h-5 w-5 mb-1.5" />
                        <span className="text-xs">Add Asset</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="h-auto flex-col p-4">
                      <Link to="/inspections/new">
                        <ClipboardCheck className="h-5 w-5 mb-1.5" />
                        <span className="text-xs">New Inspection</span>
                      </Link>
                    </Button>
                  </>
                )}
                {data.enabled_modules.safety && (
                  <Button asChild variant="outline" className="h-auto flex-col p-4">
                    <Link to="/safety/jsas/new">
                      <FileText className="h-5 w-5 mb-1.5" />
                      <span className="text-xs">New JSA</span>
                    </Link>
                  </Button>
                )}
                {data.enabled_modules.crew && canCreateTechs && (
                  <Button asChild variant="outline" className="h-auto flex-col p-4">
                    <Link to="/crew/techs/new">
                      <UserPlus className="h-5 w-5 mb-1.5" />
                      <span className="text-xs">Add Tech</span>
                    </Link>
                  </Button>
                )}
                {data.enabled_modules.projects && (
                  <Button asChild variant="outline" className="h-auto flex-col p-4">
                    <Link to="/projects/new">
                      <Briefcase className="h-5 w-5 mb-1.5" />
                      <span className="text-xs">New Project</span>
                    </Link>
                  </Button>
                )}
                {data.enabled_modules.payroll && (
                  <Button asChild variant="outline" className="h-auto flex-col p-4">
                    <Link to="/payroll/expenses/new">
                      <Receipt className="h-5 w-5 mb-1.5" />
                      <span className="text-xs">New Expense</span>
                    </Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function ActivityIcon({ action }: { action: string }) {
  switch (action) {
    case 'create':
      return <Plus className="h-4 w-4 text-green-600" />;
    case 'update':
      return <ClipboardCheck className="h-4 w-4 text-blue-600" />;
    case 'delete':
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
}
